/**
 * Type-safe template data for EIP712 signature request modal
 */

import type { ProjectInfo } from '../../lib/types'
import type { TemplateData } from '../template-engine'

export interface EIP712TypedData {
  types: Record<string, any>
  primaryType: string
  domain: Record<string, any>
  message: Record<string, any>
}

export interface SignTxTemplateData extends TemplateData {
  // Metadata
  metadataLogo: string
  metadataName: string

  // User information
  userAvatar: string
  userName: string

  // Domain verification
  origin: string
  isVerifiedOrigin: boolean

  // EIP712 Data
  primaryType: string
  messageFields: string
  domainFields: string
  fullMessageJson: string
}

/**
 * Build template data for EIP712 signature request
 */
export function buildSignTxTemplateData(
  project: ProjectInfo,
  origin: string,
  typedData: EIP712TypedData,
  metadata: { name?: string; logo?: string } | null,
  userProfile?: { avatar?: string | null; displayName?: string | null }
): SignTxTemplateData {
  const isVerifiedOrigin = project.domains.includes(origin)

  // Use metadata if available, fallback to project info
  const metadataName = metadata?.name || project.name
  const metadataLogo = metadata?.logo || project.logoUrl || ''

  // User avatar/logo for right side
  const userAvatar = userProfile?.avatar || ''
  const userName = userProfile?.displayName || 'embarkAI Wallet'

  // Helper function to format field values
  const formatFieldValue = (value: any): string => {
    if (Array.isArray(value)) {
      return `[${value.map((v) => formatFieldValue(v)).join(', ')}]`
    }
    if (typeof value === 'bigint') {
      return value.toString()
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  // Create message fields HTML
  const messageFields = Object.entries(typedData.message)
    .map(
      ([key, value]) => `
        <div class="eip712-field">
          <div class="field-name">${key}:</div>
          <div class="field-value">${formatFieldValue(value)}</div>
        </div>
      `
    )
    .join('')

  // Create domain fields HTML
  const domainFields = Object.entries(typedData.domain)
    .filter(([_, value]) => value !== undefined)
    .map(
      ([key, value]) => `
        <div class="eip712-field">
          <div class="field-name">${key}:</div>
          <div class="field-value">${formatFieldValue(value)}</div>
        </div>
      `
    )
    .join('')

  return {
    metadataLogo,
    metadataName,
    userAvatar,
    userName,
    origin,
    isVerifiedOrigin,
    primaryType: typedData.primaryType,
    messageFields,
    domainFields,
    fullMessageJson: JSON.stringify(typedData.message, null, 2)
  }
}
